import React, { useCallback, useEffect, useMemo, useState } from "react";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { saveAs } from "file-saver";
import "./VisitPlanReports.css";

const today = () => new Date().toISOString().slice(0, 10);
const thisMonth = () => today().slice(0, 7);

const getErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data?.message === "string") return data.message;
  if (typeof data === "string" && data.trim()) return data;
  if (typeof error?.message === "string" && error.message.trim()) return error.message;
  return fallback;
};

const responseError = (data, fallback) =>
  new Error(typeof data?.message === "string" ? data.message : fallback);

const formatDuration = (minutes) => {
  if (minutes === null || minutes === undefined || minutes < 0) return "-";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return remaining ? `${hours} hr ${remaining} min` : `${hours} hr`;
};

const VisitPlanReports = ({ embedded = false }) => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [reportType, setReportType] = useState("DRAFT");
  const [periodMode, setPeriodMode] = useState("DATE");
  const [date, setDate] = useState(today());
  const [month, setMonth] = useState(thisMonth());
  const [rangeFrom, setRangeFrom] = useState(today());
  const [rangeTo, setRangeTo] = useState(today());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  const selectedRange = useMemo(() => {
    if (periodMode === "DATE") return { from: date, to: date };
    if (periodMode === "MONTH") {
      const [year, monthNumber] = month.split("-").map(Number);
      const lastDay = new Date(year, monthNumber, 0).getDate();
      return { from: `${month}-01`, to: `${month}-${String(lastDay).padStart(2, "0")}` };
    }
    return { from: rangeFrom, to: rangeTo };
  }, [date, month, periodMode, rangeFrom, rangeTo]);

  const loadReport = useCallback(async () => {
    if (!selectedRange.from || !selectedRange.to) return;
    setLoading(true);
    setError("");
    try {
      const response = await fetch(
        `${API_BASE_URL}/visit-plan/reporting/preview?${new URLSearchParams({
          type: reportType,
          from: selectedRange.from,
          to: selectedRange.to,
        })}`,
        { credentials: "include" }
      );
      const data = await response.json().catch(() => null);
      if (!response.ok) throw responseError(data, "Could not generate the report.");
      setReport(data);
    } catch (requestError) {
      setReport(null);
      setError(getErrorMessage(requestError, "Could not generate the report."));
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, reportType, selectedRange]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const exportReport = async () => {
    setExporting(true);
    setError("");
    try {
      const response = await fetch(
        `${API_BASE_URL}/visit-plan/reporting/export?${new URLSearchParams({
          type: reportType,
          from: selectedRange.from,
          to: selectedRange.to,
        })}`,
        { credentials: "include" }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw responseError(data, "Could not export the report.");
      }
      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") || "";
      const match = disposition.match(/filename="?([^";]+)"?/i);
      saveAs(blob, match?.[1] || `Visit_Plan_${reportType}.xlsx`);
    } catch (requestError) {
      setError(getErrorMessage(requestError, "Could not export the report."));
    } finally {
      setExporting(false);
    }
  };

  const rows = report?.rows || [];
  const isTravelReport = reportType === "TRAVEL";

  return (
    <div className="visit-reports">
      <header className={`visit-reports-header ${embedded ? "visit-reports-header--embedded" : ""}`}>
        <div>
          <span>VISIT PLANNING</span>
          <h1><AssessmentOutlinedIcon /> Visit Plan Reports</h1>
        </div>
        <button onClick={exportReport} disabled={exporting || !report} title="Export Excel report">
          <FileDownloadOutlinedIcon fontSize="small" />
          {exporting ? "Exporting..." : "Export Excel"}
        </button>
      </header>

      <section className="visit-reports-controls">
        <label>
          <span>Report</span>
          <select value={reportType} onChange={(event) => setReportType(event.target.value)}>
            <option value="DRAFT">Created draft plans</option>
            <option value="APPROVED">Approved plans</option>
            <option value="ELIGIBLE">Eligible open complaints</option>
            <option value="TRAVEL">Visitor travel sequence</option>
          </select>
        </label>
        <label>
          <span>Period</span>
          <select value={periodMode} onChange={(event) => setPeriodMode(event.target.value)}>
            <option value="DATE">Single date</option>
            <option value="MONTH">Monthly</option>
            <option value="RANGE">Date range</option>
          </select>
        </label>

        {periodMode === "DATE" && (
          <label><span>Date</span><input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
        )}
        {periodMode === "MONTH" && (
          <label><span>Month</span><input type="month" value={month} onChange={(e) => setMonth(e.target.value)} /></label>
        )}
        {periodMode === "RANGE" && (
          <>
            <label><span>From</span><input type="date" value={rangeFrom} onChange={(e) => setRangeFrom(e.target.value)} /></label>
            <label><span>To</span><input type="date" value={rangeTo} onChange={(e) => setRangeTo(e.target.value)} /></label>
          </>
        )}

        <button className="visit-reports-generate" onClick={loadReport} disabled={loading} title="Generate report">
          <SearchIcon fontSize="small" /> {loading ? "Loading..." : "Generate"}
        </button>
      </section>

      {error && <div className="visit-reports-error">{error}</div>}

      <section className="visit-reports-results">
        <div className="visit-reports-results-header">
          <h2>Report Results</h2>
          <span>{report?.totalRecords || 0} records</span>
          <span>{selectedRange.from} to {selectedRange.to}</span>
        </div>

        <div className="visit-reports-table-wrap">
          <table>
            <thead>
              {isTravelReport ? (
                <tr>
                  <th>S.No</th><th>Visit Date</th><th>Visitor</th><th>Station</th><th>Step</th>
                  <th>From</th><th>To</th><th>Distance KM</th><th>Duration</th><th>Route Note</th>
                  <th>Bank</th><th>Branch Code</th><th>Branch Name</th><th>City</th><th>Status</th>
                  <th>Courier Status</th><th>Priority</th><th>Plan Status</th><th>Complaint ID</th>
                </tr>
              ) : (
                <tr>
                  <th>S.No</th><th>Report Date</th><th>Created By</th>
                  <th>Bank</th><th>Branch Code</th><th>Branch Name</th>
                  <th>City</th><th>Visitor</th><th>Station</th><th>Complaint Status</th>
                  <th>Courier Status</th><th>Age</th><th>HW Age</th><th>Priority</th><th>Complaint ID</th>
                </tr>
              )}
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr><td colSpan={isTravelReport ? 19 : 15} className="visit-reports-empty">No records found for this period.</td></tr>
              )}
              {rows.map((row, index) => (
                isTravelReport ? (
                  <tr key={`${row.reportDate}-${row.visitorName}-${row.stepNo}-${row.complaintId}-${index}`}>
                    <td>{index + 1}</td><td>{row.reportDate || "-"}</td><td>{row.visitorName || "-"}</td>
                    <td>{row.station || "-"}</td><td>{row.stepNo ?? "-"}</td><td>{row.fromLocation || "-"}</td>
                    <td>{row.toLocation || "-"}</td><td>{row.routeDistanceKm ?? "-"}</td>
                    <td>{formatDuration(row.routeDurationMinutes)}</td><td>{row.routeNote || "-"}</td>
                    <td>{row.bankName || "-"}</td><td>{row.branchCode || "-"}</td><td>{row.branchName || "-"}</td>
                    <td>{row.city || "-"}</td><td>{row.complaintStatus || "-"}</td><td>{row.courierStatus || "N/A"}</td>
                    <td>{row.priority || "-"}</td><td>{row.planStatus || "-"}</td><td>{row.complaintId || "-"}</td>
                  </tr>
                ) : (
                  <tr key={`${row.reportDate}-${row.complaintId}-${index}`}>
                    <td>{index + 1}</td><td>{row.reportDate || "-"}</td>
                    <td>{row.createdBy || "-"}</td><td>{row.bankName || "-"}</td>
                    <td>{row.branchCode || "-"}</td><td>{row.branchName || "-"}</td><td>{row.city || "-"}</td>
                    <td>{row.visitorName || "-"}</td><td>{row.station || "-"}</td><td>{row.complaintStatus || "-"}</td>
                    <td>{row.courierStatus || "N/A"}</td><td>{row.complaintAge ?? "-"}</td>
                    <td>{row.hardwareDeliveryAge >= 0 ? row.hardwareDeliveryAge : "-"}</td><td>{row.priority || "-"}</td>
                    <td>{row.complaintId || "-"}</td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
        {report?.truncated && <div className="visit-reports-limit">Preview limited to 500 rows. Excel includes all records.</div>}
      </section>
    </div>
  );
};

export default VisitPlanReports;


